import { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type {
  default as Highcharts,
  PointClickCallbackFunction,
} from 'highcharts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  formatDimValue,
  resolveDimRating,
} from 'src/pages/enterprise-risk/useLabelledFields';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';
import { useScoringSettings } from 'src/ratings/useScoringSettings';

import { riskDetailsUrl } from '@/utils/urls';

import {
  ENCODED_BUBBLE_SIZE,
  FIXED_BUBBLE_SIZE,
  useGetBubbleChartDefaultOptions,
} from '../../../../components/highcharts/hooks/useGetBubbleChartDefaultOptions';
import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import { dataSources } from '../../universal-widget/data-sources';
import type {
  RiskBubbleCluster,
  RiskBubbleClusterModalProps,
} from './parts/RiskBubbleClusterModal';
import type { RatingBandRegion } from './ratingBandRegions';
import { buildRatingBandPlane } from './ratingBandRegions';
import type { RiskBubbleAxis } from './riskBubbleChartOptions';
import {
  buildRiskBubbleChartOptions,
  readRiskBubbleCustom,
} from './riskBubbleChartOptions';
import type {
  BubbleSizeEncoding,
  RiskBubbleChartSettings,
} from './riskBubbleChartSettingsSchema';
import { resolveBubbleSizeEncoding } from './riskBubbleChartSettingsSchema';
import type { RiskBubblePoint } from './riskBubblePoints';
import { buildRiskBubblePoints, countChildRisks } from './riskBubblePoints';
import { useRatingCellOptions } from './useRatingCellOptions';

interface UseRiskBubbleChartOptions {
  controlType: RiskAssessmentResultControlType;
  settings: RiskBubbleChartSettings | null;
}

export interface RiskBubbleChartState {
  loading: boolean;
  points: RiskBubblePoint[];
  regions: RatingBandRegion[];
  sizeEncoding: BubbleSizeEncoding;
  options: Highcharts.Options;
  emptyState: { title: string; body: string };
  cluster: RiskBubbleCluster | null;
  clusterCopy: RiskBubbleClusterModalProps['copy'];
  openRisk: (id: string) => void;
  dismissCluster: () => void;
}

/**
 * Every risk the viewer may see, at its exact likelihood and impact, over the
 * rating bands of the current matrix. Data comes through the same
 * permission-filtered register query as every other risk widget, and the
 * bands from the shared band rule over the matrix's declared coordinates.
 * Bubble size carries whatever the widget's settings say it should
 * (RSP-7292); nothing about a bubble's position depends on that.
 */
export const useRiskBubbleChart = ({
  controlType,
  settings,
}: UseRiskBubbleChartOptions): RiskBubbleChartState => {
  const { t, i18n } = useTranslation(['common'], {
    keyPrefix:
      controlType === RiskAssessmentResultControlType.Controlled
        ? 'dashboard.widgets.controlledRiskBubbleChart'
        : 'dashboard.widgets.uncontrolledRiskBubbleChart',
  });
  const { t: chartT } = useTranslation(['common'], {
    keyPrefix: 'dashboard.riskBubbleChart',
  });
  // The size option labels are read from the settings modal's own keys, so
  // the chart can never call a measure something the setting does not.
  const { t: sizeT } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgetSettings.fields.bubbleSizeOptions',
  });
  const { t: commonT } = useTranslation(['common']);
  const locale = i18n.language;
  const navigate = useNavigate();
  const [cluster, setCluster] = useState<RiskBubbleCluster | null>(null);

  const {
    tableProps: { allItems },
    data,
    loading,
  } = useGetWidgetData({
    dataSource: dataSources.risk,
    propertyFilterQuery: settings?.filtering,
  });
  const { latestConfigId, loading: scoringSettingsLoading } =
    useScoringSettings({ configType: 'risk_assessment' });
  const {
    options: { likelihood: likelihoodOptions, impact: impactOptions },
    resolveLikelihood,
    resolveImpact,
  } = useRiskRatingResolver('standard', { configType: 'risk_assessment' });
  const cellOptions = useRatingCellOptions(controlType);

  const sizeEncoding = resolveBubbleSizeEncoding(settings);
  // Counted over the rows before the widget's own filter, so a chart filtered
  // to one tier still sizes that tier by the children it cannot show.
  const childCounts = useMemo(() => countChildRisks(data?.risk), [data]);

  const points = useMemo(
    () =>
      buildRiskBubblePoints(allItems, controlType, latestConfigId, {
        encoding: sizeEncoding,
        childCounts,
      }),
    [allItems, controlType, latestConfigId, sizeEncoding, childCounts]
  );

  const plane = useMemo(
    () =>
      buildRatingBandPlane(cellOptions, {
        likelihoods: points.map((point) => point.likelihood),
        impacts: points.map((point) => point.impact),
      }),
    [cellOptions, points]
  );

  const formatLikelihood = useCallback(
    (value: number) => resolveDimRating(value, locale, resolveLikelihood).label,
    [locale, resolveLikelihood]
  );
  const formatImpact = useCallback(
    (value: number) => resolveDimRating(value, locale, resolveImpact).label,
    [locale, resolveImpact]
  );
  const formatMeasure = useCallback(
    (value: number) => formatDimValue(value, locale),
    [locale]
  );

  const likelihoodAxis = useMemo<RiskBubbleAxis>(
    () => ({
      title: t('yAxisTitle'),
      ticks: likelihoodOptions.map((option) => ({
        value: option.value,
        label: formatLikelihood(option.value),
      })),
      extent: plane.extent?.likelihood,
    }),
    [t, likelihoodOptions, formatLikelihood, plane.extent]
  );
  const impactAxis = useMemo<RiskBubbleAxis>(
    () => ({
      title: t('xAxisTitle'),
      ticks: impactOptions.map((option) => ({
        value: option.value,
        label: formatImpact(option.value),
      })),
      extent: plane.extent?.impact,
    }),
    [t, impactOptions, formatImpact, plane.extent]
  );

  const openRisk = useCallback(
    (id: string) => {
      setCluster(null);
      navigate(riskDetailsUrl(id));
    },
    [navigate]
  );
  const dismissCluster = useCallback(() => setCluster(null), []);

  // A bubble standing for one risk opens it; one standing for several lets
  // the reader choose which (RSP-7294).
  const onPointClick = useCallback<PointClickCallbackFunction>(
    (event) => {
      const custom = readRiskBubbleCustom(event.point.options.custom);
      if (!custom) {
        return;
      }
      if (custom.members.length > 1) {
        setCluster({
          members: custom.members,
          likelihood: custom.likelihood,
          impact: custom.impact,
          rating: custom.rating,
        });
      } else if (custom.id) {
        openRisk(custom.id);
      }
    },
    [openRisk]
  );
  const defaults = useGetBubbleChartDefaultOptions({
    onPointClick,
    bubbleSize:
      sizeEncoding === 'fixed' ? FIXED_BUBBLE_SIZE : ENCODED_BUBBLE_SIZE,
  });

  const clusterName = useCallback(
    (count: number) => chartT('clusterName', { count }),
    [chartT]
  );

  const options = useMemo(
    () =>
      buildRiskBubbleChartOptions({
        defaults,
        points,
        regions: plane.regions,
        likelihoodAxis,
        impactAxis,
        size: {
          encoding: sizeEncoding,
          label: sizeT(sizeEncoding),
          formatMeasure,
        },
        copy: {
          likelihood: t('popover.likelihood'),
          impact: t('popover.impact'),
          rating: t('popover.rating'),
          worse: chartT('worse'),
          clusterName,
        },
        formatLikelihood,
        formatImpact,
      }),
    [
      defaults,
      points,
      plane.regions,
      likelihoodAxis,
      impactAxis,
      sizeEncoding,
      sizeT,
      formatMeasure,
      t,
      chartT,
      clusterName,
      formatLikelihood,
      formatImpact,
    ]
  );

  return {
    loading: loading || scoringSettingsLoading,
    points,
    regions: plane.regions,
    sizeEncoding,
    options,
    emptyState: { title: t('noData.title'), body: t('noData.body') },
    cluster,
    clusterCopy: {
      title: cluster ? clusterName(cluster.members.length) : '',
      likelihood: t('popover.likelihood'),
      impact: t('popover.impact'),
      rating: t('popover.rating'),
      close: commonT('close'),
      closeModal: commonT('closeModal'),
    },
    openRisk,
    dismissCluster,
  };
};
